import { useState, useEffect } from 'react';
import axios from 'axios';
import { EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';
import Navbar from '../components/Navbar';
import Hero from '../components/Hero';
import SearchBar from '../components/SearchBar';
import CarCard from '../components/CarCard';
import Footer from '../components/Footer';

export default function Home() {
  const [recentCars, setRecentCars] = useState([]);
  const [allCars, setAllCars] = useState([]);
  const [randomCars, setRandomCars] = useState([]); // Dynamic state to house 6 random cars
  const [activeTab, setActiveTab] = useState('new'); // For Explore Our Fleet
  const [fleetPage, setFleetPage] = useState(1); // Page tracking for Explore Our Fleet
  const fleetCarsPerPage = 6; // Yields exactly 2 rows x 3 columns on desktop (6 cards)
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subscribedUsers, setSubscribedUsers] = useState([]);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchRecentCars = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/cars/recent`);
        setRecentCars(res.data);
      } catch (err) {
        console.error('Error fetching recent cars:', err.message);
      }
    };

    const fetchAllCars = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/cars/all`);
        setAllCars(res.data);
        
        // Select exactly 6 random cars once on successfully loading the data
        if (res.data && res.data.length > 0) {
          const shuffled = [...res.data].sort(() => 0.5 - Math.random());
          setRandomCars(shuffled.slice(0, 6));
        }
      } catch (err) {
        console.error('Error fetching all cars:', err.message);
      }
    };

    fetchRecentCars();
    fetchAllCars();
    const interval = setInterval(() => {
      fetchRecentCars();
      fetchAllCars();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Reset fleet page to 1 whenever tabs are changed
  useEffect(() => {
    setFleetPage(1);
  }, [activeTab]);

  const handleTypeClick = (type) => {
    window.location.href = `/browse?body_type=${encodeURIComponent(type)}`;
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/newsletter`, { name, email });
      setSubscribedUsers([...subscribedUsers, { name, email }]);
      setMessage('Subscription successful!');
      setName('');
      setEmail('');
    } catch (err) {
      setMessage('Subscription failed. Please try again.');
      console.error('Error subscribing:', err.message);
    }
  };

  const getCarsByCondition = (condition) => {
    return allCars.filter(car => {
      const cond = (car.condition || '').toLowerCase().trim();
      // Safely map standard condition formats
      if (condition === 'foreign used' || condition === 'fairly used') {
        return cond.includes('foreign') || cond.includes('fairly') || cond === 'used';
      }
      return cond === condition.toLowerCase().trim();
    });
  };

  // Pagination bounds calculation for "Explore Our Fleet"
  const filteredFleetCars = getCarsByCondition(activeTab);
  const totalFleetPages = Math.ceil(filteredFleetCars.length / fleetCarsPerPage);
  const paginatedFleetCars = filteredFleetCars.slice(
    (fleetPage - 1) * fleetCarsPerPage,
    fleetPage * fleetCarsPerPage
  );

  const handleFleetPageChange = (page) => {
    setFleetPage(page);
    const element = document.getElementById('explore-fleet-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="bg-gray-50/50 text-gray-900 min-h-screen selection:bg-red-600 selection:text-white antialiased overflow-x-hidden">
      
      {/* Contact Strip with Safe Sized Icons & Wrapped Complete Phone Numbers */}
      <div className="bg-black text-gray-300 text-[11px] sm:text-xs py-2.5 px-4 sm:px-8 border-b border-gray-900">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:justify-between items-center gap-3">
          <div className="flex flex-wrap justify-center items-center gap-x-5 gap-y-2 text-center md:text-left w-full md:w-auto">
            
            {/* Unified Email */}
            <a href="mailto:info@sajies.ng" className="flex items-center hover:text-red-500 transition-colors shrink-0">
              <EnvelopeIcon className="h-4 w-4 mr-1.5 text-blue-500" />
              info@sajies.ng
            </a>
            
            {/* Unified Phones - Uses a single phone icon for all three clickable numbers */}
            <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
              <PhoneIcon className="h-4 w-4 text-blue-500 mr-1 flex-shrink-0" />
              <a href="tel:+2348138576446" className="hover:text-red-500 transition-colors">(+234) 8138576446</a>
              <span className="text-gray-700 select-none">|</span>
              <a href="tel:+2348033697408" className="hover:text-red-500 transition-colors">(+234) 8033697408</a>
              <span className="text-gray-700 select-none">|</span>
              <a href="tel:+2347010640278" className="hover:text-red-500 transition-colors">(+234) 7010640278</a>
            </div>
          </div>
          
          {/* Social Icons Constrained to 20px (h-5 w-5) to Prevent Large Native Layout Shifts */}
          <div className="flex items-center space-x-4 flex-shrink-0">
            <a href="https://facebook.com/sajiesglobalsynergy" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform flex items-center">
              <img src='./img/facebook.svg' alt="Facebook" className="h-5 w-5 max-h-5 max-w-5 object-contain invert" />
            </a>
            <a href="https://instagram.com/sajiesglobalsynergy" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform flex items-center">
              <img src='./img/instagram.svg' alt="Instagram" className="h-5 w-5 max-h-5 max-w-5 object-contain invert" />
            </a>
            <a href="https://tiktok.com/@sajiesglobalsynergy" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform bg-white/50 p-1 rounded-full flex items-center justify-center">
              <img src='./img/tiktok.svg' alt="TikTok" className="h-4 w-4 max-h-4 max-w-4 object-contain" />
            </a>
          </div>
        </div>
      </div>

      <Navbar />
      <Hero />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        <SearchBar />

        {/* Search by Body Type */}
        <section className="my-16">
          <div className="flex flex-col items-center text-center mb-10">
            <h2 className="text-3xl font-extrabold text-blue-950 tracking-tight font-sans">
              SELECT A BODY TYPE
            </h2>
            <div className="h-1 w-16 bg-red-600 mt-3 rounded"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {['SUV', 'Sedan', 'Truck', 'Coupe'].map(type => (
              <button
                key={type}
                onClick={() => handleTypeClick(type)}
                className="bg-white p-6 rounded-xl border border-gray-200/80 shadow-sm hover:border-blue-900 hover:shadow-md transition-all duration-300 flex flex-col items-center justify-center group"
              >
                <div className="w-24 h-24 mb-3 flex items-center justify-center bg-gray-50 rounded-lg group-hover:bg-blue-50/50 transition-colors">
                  <img
                    src={`./img/${type === 'SUV' ? 'SUV-blue' : type === 'Sedan' ? 'Sedan-blue' : type === 'Truck' ? 'Truck-blue' : 'Hybrid-blue'}.svg`}
                    alt={`${type} Icon`}
                    className="w-16 h-16 object-contain group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <span className="text-base font-bold text-gray-800 tracking-wider group-hover:text-red-600 transition-colors">
                  {type}
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* Featured Cars (New Arrivals) */}
        <section className="my-20">
          <div className="flex flex-col items-center text-center mb-10">
            <h2 className="text-3xl font-extrabold text-blue-950 tracking-tight font-sans">
              NEW ARRIVALS
            </h2>
            <div className="h-1 w-16 bg-red-600 mt-3 rounded"></div>
          </div>
          
          <div className="flex overflow-x-auto space-x-6 pb-6 pt-2 scrollbar-hidden scroll touch-pan-x px-1">
            {recentCars.slice(0, 6).map(car => (
              <div
                key={car.id}
                className="min-w-[280px] sm:min-w-[320px] bg-white rounded-xl shadow-sm border border-gray-100 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg p-2 flex-shrink-0"
              >
                <CarCard car={car} />
              </div>
            ))}
          </div>
        </section>

        {/* Our Vehicle Collection (Randomly Picked, 2 rows, 3 columns on desktop) */}
        <section className="my-20">
          <div className="flex flex-col items-center text-center mb-10">
            <h2 className="text-3xl font-extrabold text-blue-950 tracking-tight font-sans">
              OUR VEHICLE COLLECTION
            </h2>
            <div className="h-1 w-16 bg-red-600 mt-3 rounded"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {randomCars.map(car => (
              <div
                key={car.id}
                className="bg-white rounded-xl shadow-sm border border-gray-100 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg p-2 animate-fadeIn"
              >
                <CarCard car={car} />
              </div>
            ))}
          </div>
        </section>

        {/* Explore Our Fleet (New, Fairly Used, Nigerian Used with Pagination, 2 rows, 3 columns on desktop) */}
        <section id="explore-fleet-section" className="my-24 bg-white rounded-2xl p-6 sm:p-10 border border-gray-100 shadow-sm scroll-mt-24">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-blue-950 tracking-tight font-sans">
              EXPLORE OUR FLEET
            </h2>
            <p className="text-sm sm:text-base text-gray-500 mt-2 font-light">
              Filter through pristine options and check our available options.
            </p>
            <div className="h-1 w-16 bg-red-600 mt-4 mx-auto rounded"></div>
          </div>

          {/* Condition Category Buttons */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {[
              { id: 'new', label: 'New Cars' },
              { id: 'foreign used', label: 'Fairly Used Cars' },
              { id: 'nigerian used', label: 'Nigerian Used Cars' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-250 shadow-sm border ${
                  activeTab === tab.id 
                    ? 'bg-blue-900 text-white border-blue-900' 
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedFleetCars.length > 0 ? (
              paginatedFleetCars.map(car => (
                <div
                  key={car.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg p-2"
                >
                  <CarCard car={car} />
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-16 bg-gray-50 rounded-xl">
                <p className="text-gray-500 font-medium">Currently no vehicles available in this category.</p>
              </div>
            )}
          </div>

          {/* Fleet Pagination controls - fully wrapping and bound to prevent off-screen page shifting */}
          {totalFleetPages > 1 && (
            <div className="flex flex-wrap justify-center gap-2 mt-12 max-w-full px-4">
              {Array.from({ length: totalFleetPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => handleFleetPageChange(page)}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg shadow-sm transition-all duration-200 shrink-0 ${
                    fleetPage === page
                      ? 'bg-blue-900 text-white font-bold shadow'
                      : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Newsletter Subscription Section */}
        <section className="my-24 bg-gradient-to-r from-blue-950 to-slate-950 text-white p-8 sm:p-12 rounded-2xl shadow-xl relative overflow-hidden">
          <div className="absolute right-0 bottom-0 top-0 w-1/3 bg-radial-gradient from-red-600/10 to-transparent pointer-events-none"></div>
          <div className="max-w-4xl mx-auto flex flex-col items-center text-center">
            <span className="text-xs font-bold text-red-500 tracking-widest uppercase mb-2">STAY UPDATED</span>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight font-sans text-white">
              SUBSCRIBE TO OUR NEWSLETTER
            </h2>
            <p className="text-sm sm:text-base text-gray-300 font-light mt-3 max-w-xl">
              Get direct alerts regarding fresh premium arrivals, luxury custom offerings, and financing deals.
            </p>

            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 w-full max-w-2xl mt-8">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full Name"
                className="px-4 py-3 bg-white/10 border border-white/20 rounded-xl w-full sm:w-1/2 text-white placeholder-gray-400 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition"
                required
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email Address"
                className="px-4 py-3 bg-white/10 border border-white/20 rounded-xl w-full sm:w-1/2 text-white placeholder-gray-400 focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition"
                required
              />
              <button
                type="submit"
                className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-3 rounded-xl transition-all duration-300 w-full sm:w-auto"
              >
                Subscribe
              </button>
            </form>

            {message && (
              <p className={`mt-4 text-sm font-semibold ${message.includes('success') ? 'text-green-400' : 'text-red-400'}`}>
                {message}
              </p>
            )}
          </div>
        </section>

        {/* Gallery Section */}
        <section className="my-20">
          <div className="flex flex-col items-center text-center mb-10">
            <h2 className="text-3xl font-extrabold text-blue-950 tracking-tight font-sans">
              OUR GALLERY
            </h2>
            <div className="h-1 w-16 bg-red-600 mt-3 rounded"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['./img/sajies_car1.jpg', './img/sajies_car2.jpg', './img/sajies_car3.jpg', './img/sajies_car4.jpg', './img/sajies_car5.jpg', './img/sajies_car6.jpg', './img/sajies_car7.jpg', './img/sajies_car8.jpg'].map((src, index) => (
              <div key={index} className="overflow-hidden rounded-xl bg-gray-100 border border-gray-100 shadow-sm relative group aspect-[4/3]">
                <img
                  src={src}
                  alt={`Sajies Fleet ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                  <span className="text-white text-xs font-semibold uppercase tracking-wider">Premium Selection</span>
                </div>
              </div>
            ))}
          </div>
        </section>

      </div>
      
      <Footer />
    </div>
  );
}
